import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { storyService } from '../services/storyService';
import StoryPlayer from './StoryPlayer';

const SharedStoryViewer = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCover, setShowCover] = useState(true);

  useEffect(() => {
    loadSharedStory();
  }, [shareId]);

  const loadSharedStory = async () => {
    try {
      setLoading(true);
      setError(null);
      const storyData = await storyService.getStoryByShareId(shareId);
      setStory(storyData);
    } catch (err) {
      console.error('Error loading shared story:', err);
      setError('Story not found or link has been revoked.');
    } finally {
      setLoading(false);
    }
  };

  const handleBeginStory = () => {
    setShowCover(false);
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleBackToCover = () => {
    setShowCover(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <p className="ml-4 text-gray-600">Loading shared story...</p>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-dreamy p-6 text-center"
      >
        <div className="mb-4 text-red-500">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-6xl mx-auto" />
        </div>
        <h2 className="text-2xl font-display font-bold text-red-700 mb-2">
          Story Not Available
        </h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 mx-auto"
          onClick={handleBack}
        >
          <SafeIcon icon={FiIcons.FiHome} className="text-lg" />
          Go Home
        </motion.button>
      </motion.div>
    );
  }

  if (!story) {
    return null;
  }

  // Show cover page first
  if (showCover) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-dreamy overflow-hidden"
      >
        {/* Header with shared story indicator */}
        <div className="bg-purple-100 border-b border-purple-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-purple-700">
              <SafeIcon icon={FiIcons.FiLink} className="text-lg" />
              <span className="font-medium">Shared Story</span>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-purple-200 hover:bg-purple-300 text-purple-700 px-3 py-1 rounded-lg text-sm font-medium"
              onClick={handleBack}
            >
              <SafeIcon icon={FiIcons.FiHome} className="text-sm mr-1" />
              Home
            </motion.button>
          </div>
          <p className="text-sm text-purple-600 mt-1">
            Shared by {story.author_email?.split('@')[0] || story.author_name || 'another user'}
          </p>
        </div>

        {/* Story Cover */}
        <div className="p-8">
          {/* Story Image */}
          {story.story_image_url && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6 rounded-2xl overflow-hidden shadow-soft"
            >
              <img
                src={story.story_image_url}
                alt={story.title}
                className="w-full h-64 md:h-80 object-cover"
              />
            </motion.div>
          )}

          {/* Story Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-5xl font-display font-bold text-purple-700 mb-4 text-center"
          >
            {story.title}
          </motion.h1>

          {/* Author Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center mb-6"
          >
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <SafeIcon icon={FiIcons.FiUser} className="text-lg" />
              <span className="font-medium">
                by {story.author_email?.split('@')[0] || story.author_name || 'Anonymous'}
              </span>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mt-1">
              <SafeIcon icon={FiIcons.FiCalendar} className="text-sm" />
              <span>Created {new Date(story.created_at).toLocaleDateString()}</span>
            </div>
          </motion.div>

          {/* Story Description */}
          {story.description && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-purple-50 rounded-2xl p-6 mb-8"
            >
              <p className="text-gray-700 leading-relaxed text-lg text-center">
                {story.description}
              </p>
            </motion.div>
          )}

          {/* Begin Story Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-primary-500 to-purple-600 hover:from-primary-600 hover:to-purple-700 text-white px-8 py-4 rounded-2xl font-bold text-xl flex items-center gap-3 mx-auto shadow-dreamy"
              onClick={handleBeginStory}
            >
              <SafeIcon icon={FiIcons.FiPlay} className="text-2xl" />
              Begin Story
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Show the actual story player
  return (
    <div>
      {/* Back to cover button */}
      <div className="mb-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
          onClick={handleBackToCover}
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="text-lg" />
          Back to Story Cover
        </motion.button>
      </div>

      {/* Use the existing StoryPlayer component */}
      <StoryPlayer storyId={story.id} sharedStory={story} />
    </div>
  );
};

export default SharedStoryViewer;